import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

interface ErrorWithMessage {
  message: string;
}

const isFetchBaseQueryError = (
  error: unknown
): error is FetchBaseQueryError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'data' in error
  );
};

const isSerializedError = (error: unknown): error is SerializedError => {
  return typeof error === 'object' && error !== null && 'message' in error;
};

const hasMessage = (data: unknown): data is ErrorWithMessage => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'message' in data &&
    typeof (data as ErrorWithMessage).message === 'string'
  );
};

export const useRtkQueryError = () => {
  const getErrorMessage = (
    error: FetchBaseQueryError | SerializedError | undefined
  ): string => {
    if (!error) return 'Error desconocido';

    if (isFetchBaseQueryError(error)) {
      if (hasMessage(error.data)) {
        return error.data.message;
      }

      if (typeof error.data === 'string') {
        return error.data;
      }

      if (error.status === 'FETCH_ERROR') {
        return 'Error de conexión. Verifica tu red';
      }

      if (error.status === 'PARSING_ERROR') {
        return 'Error al procesar la respuesta del servidor';
      }

      if (typeof error.status === 'number') {
        return `Error del servidor (${error.status})`;
      }
    }

    if (isSerializedError(error) && error.message) {
      return error.message;
    }

    return 'Ocurrió un error al cargar los datos';
  };

  return { getErrorMessage };
};
